import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { loadAppearance } from "../../appearance";

import { WIKI_LAYOUTS } from "./forgeWikiLayouts";
import { useForgeWikiStage } from "./useForgeWikiStage";
import { useForgeGpuStage } from "./useForgeGpuStage";

/* one array, shared, so a page that never searches hands the same empty list
   over on every render */
const EMPTY = [];

/**
 * A card's own tint.
 *
 * The periodic table gives each of its tiles the same colour at a random
 * strength - `rgba(0,127,127, random·0.5 + 0.25)` - which is what stops a
 * sphere of a hundred tiles reading as one flat mass. The same idea, in this
 * product's colours: a hue out of the AppFactor palette (the accent, the
 * ember, the rust, the cocoa, the espresso and the peach) mixed into the
 * card's own surface at a strength between 6% and 20%, which is enough to tell
 * two cards apart across the stage and not enough to disturb what is written
 * on them.
 *
 * Random per card, but drawn from the card's own id rather than from
 * Math.random: a colour that changed on every render would flicker through
 * each layout change, and a codebase would not be the same colour twice. The
 * same hash gives the card its weight - see the opacity below.
 */
/* Ten of them, defined per theme in ForgeWiki.css (see "The cards' palette"):
   six lifts of the dashboard's own ink and four from its accent and rust. */
const TINT_COUNT = 10;

export function cardSeed(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h >>>= 0;
  /* And WHERE IN THE RANGE this card sits, which is the second thing that
     stops a hundred of them reading as one sheet - the ones behind sit back a
     little. A fraction rather than a weight, because the range itself is
     somebody's setting now (Appearance > Card fill from/to): this says which
     card is palest and which is most solid, and the setting says what those
     two words are worth. Drawn from the id so a card keeps its place in the
     spread whatever the range is moved to.

     The writing stays at full strength whatever the surface does, and an
     opened card's surface goes solid (see .forge-wiki-card::before). */
  const t = ((h >>> 20) % 24) / 23;
  return { tint: h % TINT_COUNT, t, opacity: Math.round((0.75 + t * 0.23) * 100) / 100 };
}

/**
 * A card's surface, as the two custom properties that paint it.
 * ---------------------------------------------------------------------------
 * One place, because there are two ways to paint a weight and three callers
 * that need both of them:
 *
 *   as transparency   the tint at that alpha, over whatever is behind it -
 *                     which on a shelf is the page and on a sphere is thirty
 *                     other cards
 *   as a colour       the same tint already mixed with the page's own ground
 *                     in that proportion, painted solid - the same colour to
 *                     the eye, with nothing showing through it
 *
 * `color-mix` is what does the mixing, and it is left to CSS rather than
 * worked out here on purpose: the tint may be `var(--wiki-tint-7)`, which has
 * a different value per theme and is not a colour this file could resolve.
 * The GPU renderer resolves the whole expression off the document when it
 * draws its faces (see forgeCardFace), so both get the same answer.
 * ---------------------------------------------------------------------------
 */
export function cardFill(tint, opacity, { solid } = {}) {
  /* Glass is transparency, so a glass card cannot also be a solid one: with
     the pane switched on, the weight stays a weight and the stylesheets spend
     a third of it (see "or a pane of glass" in ForgeWiki.css). */
  const glass = loadAppearance("glass") >= 1;
  const flat = glass
    ? false
    : (typeof solid === "boolean" ? solid : loadAppearance("solid") >= 1);
  const weight = Math.max(0, Math.min(1, opacity));

  if (!flat) {
    return {
      "--forge-wiki-card-tint": tint,
      "--forge-wiki-card-opacity": Math.round(weight * 100) / 100,
    };
  }
  return {
    "--forge-wiki-card-tint":
      `color-mix(in srgb, ${tint} ${Math.round(weight * 100)}%, var(--forge-surface))`,
    "--forge-wiki-card-opacity": 1,
  };
}

/** Where in the range a card sits, as the range is set now. */
export function cardWeight(seed, { from, to } = {}) {
  const lo = typeof from === "number" ? from : loadAppearance("alphaFrom");
  const hi = typeof to === "number" ? to : loadAppearance("alphaTo");
  /* either order: dragging the bottom past the top swaps them rather than
     collapsing every card onto one weight */
  const [a, b] = lo <= hi ? [lo, hi] : [hi, lo];
  return a + cardSeed(seed).t * (b - a);
}

/* The same look, in the CSS stage's own terms: the tint and the weight go to
   the surface layer (.forge-wiki-card::before), so the writing on the card is
   never faded with it.

   `colors` is a palette's ten swatches, when one has been chosen (see
   cardPalette): the card's own tint index picks one of them instead of the
   estate's tint variables. Ten either way, so the same card keeps the same
   place in whichever set is in use. */
/**
 * The look of one card: which swatch, and how solid.
 *
 * @param opts  { from, to, solid } - the "Card fill" range and whether to
 *              spend the weight on a colour rather than on transparency.
 *              Left out, the settings' own values are read.
 */
export function cardLook(seed, colors, opts = {}) {
  const look = cardSeed(seed);
  const tint = Array.isArray(colors) && colors.length
    ? colors[look.tint % colors.length]
    : `var(--wiki-tint-${look.tint + 1})`;
  return cardFill(tint, cardWeight(seed, opts), opts);
}

/**
 * How solid a card painted in a severity's colour is.
 *
 * Between 0.80 and 0.95, and decided by the card's own id rather than by
 * Math.random: a weight that changed on every render would have the stage
 * shimmering as the cards redraw. The variation is the point - a wall of
 * forty identical reds reads as one shape - and so is the floor: a severity
 * has to be legible as itself, which is why it does not go below 0.80.
 */
export function severityAlpha(seed) {
  let h = 2166136261;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h >>>= 0;
  return (80 + ((h >>> 11) % 16)) / 100;
}

/* ── What colour the writing on a card has to be ─────────────────────────
   A card's surface is not the swatch it was given: the tint layer is drawn at
   the card's own weight over the page's ground, so what the type actually sits
   on is the two composited. That composite is what decides the ink - and it is
   decided, not guessed, by taking the contrast ratio the WCAG defines against
   white and against this product's ink and keeping whichever is higher.

   The same reckoning serves every card on every page: a palette swatch, a
   severity, anything that paints a card.
   ------------------------------------------------------------------------- */

/** Any CSS colour this product actually writes: #rgb, #rrggbb, rgb(), rgba(). */
function parseColor(input) {
  const str = String(input || "").trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(str);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split("").map(c => c + c).join("") : hex[1];
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
  }
  const rgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,/\s]+([\d.]+))?\s*\)$/i.exec(str);
  if (rgb) {
    return {
      r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]),
      a: rgb[4] === undefined ? 1 : Number(rgb[4]),
    };
  }
  return null;
}

const channel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

const luminance = ({ r, g, b }) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

const contrast = (a, b) => {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
};

/** One colour laid over another at `alpha`. */
const over = (fg, bg, alpha) => ({
  r: fg.r * alpha + bg.r * (1 - alpha),
  g: fg.g * alpha + bg.g * (1 - alpha),
  b: fg.b * alpha + bg.b * (1 - alpha),
});

/* The ground a card stands on, per theme - the page's own background: black on
   the dark side, the documentation's paper on the cream one. It is the theme's
   only part in this: what the type is written in depends on the colour that
   ends up under it, not on which theme the page is in. */
const GROUND = { dark: { r: 0, g: 0, b: 0 }, light: { r: 252, g: 251, b: 249 } };

/* The two inks a card can be written in. Always these two, whichever theme is
   on: a pale swatch on the dark theme wants dark type exactly as it does on
   the light one, and comparing white against white - which is what reading the
   ink off the theme amounted to - decides nothing. */
const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 23, g: 23, b: 23 };

/**
 * Which ink reads better on a given card colour.
 *
 * @returns "is-ink-light" when white wins, "is-ink-dark" when near-black does
 */
export function inkOn(color, { dark = true, weight = 1 } = {}) {
  const paint = parseColor(color);
  if (!paint) return "";
  const ground = dark ? GROUND.dark : GROUND.light;
  /* the swatch's own alpha and the card's weight both thin it */
  const surface = over(paint, ground, Math.max(0, Math.min(1, paint.a * weight)));
  const L = luminance(surface);
  return contrast(L, luminance(WHITE)) >= contrast(L, luminance(BLACK))
    ? "is-ink-light"
    : "is-ink-dark";
}

/** The same, for a card taking its colour from a palette. */
export function cardInk(seed, colors, opts = {}) {
  if (!Array.isArray(colors) || !colors.length) return "";
  const look = cardSeed(seed);
  /* The weight decides which ink reads, and it is the same number whether it
     was spent on transparency or mixed into the colour - a card that LOOKS
     85% solid takes the ink an 85% card takes. */
  return inkOn(colors[look.tint % colors.length], { ...opts, weight: cardWeight(seed, opts) });
}

/**
 * A column beside the stage, and the edge that sizes it.
 * ---------------------------------------------------------------------------
 * Both pages that put a list next to their cards let it be dragged wider: what
 * one gives the other takes, and the stage re-solves its arrangement as it
 * goes (useForgeWikiStage watches its own box). How wide it should be is a
 * fact about this screen rather than about this visit, so it is remembered.
 *
 * Returns the width, the handlers the grip needs, and the way back to the
 * width the column was designed at.
 * ---------------------------------------------------------------------------
 */
export function useSideWidth(key, { initial = 320, min = 240, maxFraction = 0.6, hostRef, side = "right" } = {}) {
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(key));
      return Number.isFinite(saved) && saved >= min ? saved : initial;
    } catch (e) { return initial; }
  });
  const drag = useRef(null);

  const remember = useCallback((w) => {
    try { localStorage.setItem(key, String(w)); } catch (e) { /* private window */ }
  }, [key]);

  const onPointerDown = useCallback((e) => {
    const host = hostRef?.current;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = {
      x: e.clientX,
      w: width,
      max: Math.round((host?.clientWidth || window.innerWidth) * maxFraction),
    };
  }, [width, hostRef, maxFraction]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    /* Which way is wider depends on which side the column is on: a panel on
       the right grows as its edge is dragged left, and one on the left grows
       as the same edge is dragged right. The gesture is the same either
       way - pull the edge away from the page's edge. */
    const by = side === "left" ? e.clientX - d.x : d.x - e.clientX;
    setWidth(Math.min(Math.max(d.w + by, min), d.max));
  }, [min, side]);

  const onPointerUp = useCallback((e) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    remember(width);
  }, [width, remember]);

  const reset = useCallback(() => { setWidth(initial); remember(initial); }, [initial, remember]);

  return {
    width,
    gripProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick: reset,
    },
  };
}

/**
 * A stage of cards, and everything it answers to.
 * ---------------------------------------------------------------------------
 * The wiki built this: a set of cards standing in one of three arrangements,
 * turned by the pointer, walked by the arrow keys, and taken up one at a time
 * into a rail with the thing it describes open beside it. None of that is
 * about documentation - it is about having a set of cards - so it lives here,
 * where a second page can have the same stage with its own cards on it.
 *
 * What stays with the page: the cards' own markup, what the preview says, what
 * the panel beside the rail shows, and where the search box lives. What comes
 * from here: the state they are all drawn from, and every behaviour that acts
 * on the set rather than on one card -
 *
 *     1 2 3         the arrangements, the third press of one squaring the
 *                   camera up again
 *     drag / wheel  turn and zoom (useForgeWikiStage's own doing)
 *     Space         fit what is on the stage into it; twice, or Esc, back to
 *                   the arrangement as drawn
 *     arrows        walk the cards by where they are on screen, or walk the
 *                   rail once one is taken up
 *     Enter         take the selected card up
 *     P             the tour, turning the camera on its own
 *     F             turn every card to face the screen
 *     Ctrl+Shift+9  fit, for a hand that is not on the scene
 *     Ctrl+Shift+0  the sample copies, when the page offers them
 *
 * The one thing it does NOT own is the set itself: `count` is what the page
 * has on the stage, and `matches` which of those the search is pointing at.
 * ---------------------------------------------------------------------------
 */
export function useForgeCardScene({
  /* how many cards are on the stage */
  count,
  /* which of them the search is pointing at, as indices; empty while it is
     filtering, since everything left matches by then */
  matches = EMPTY,
  /* What each card IS - one stable id per card, in the order they are drawn.
     The selection is held as one of these rather than as a position, so a
     search, a lens or a re-sort moves the highlight with the card instead of
     leaving it on whatever has arrived in that slot. A page that does not pass
     them gets positions, which is what this did before. */
  ids = EMPTY,
  /* where the renderer choice is remembered - a preference about this machine,
     so each page keeps its own */
  rendererKey,
  /* the page's root, whose controls are taken out of the tab order */
  rootSelector,
  /* Ctrl+Shift+0, when the page has something for it to do */
  onToggleSamples,
  /* a right-click on a card, for the page that answers one */
  onCardMenu,
  /* a plain click on a card, for the page that needs the click itself rather
     than the selection it usually changes */
  onPicked,
  /* how long the rail's panel waits before following the selection */
  settleMs = 320,
  /* What is ON each card, for the renderer that has to draw them itself:
     the GPU stage has no DOM to read, so the page hands it the same facts its
     own markup is built from, and the look each card would have been painted
     in. Left out, the page simply has no GPU mode. */
  tiles = EMPTY,
  looks = EMPTY,
  /* and what the search is pointing at, so the ones it passed over step back
     on the canvas as they do in the document */
  query = "",
}) {
  /* The arrangement the cards stand in, and the scene that carries them
     there. The stage owns every card's `transform` from here on. */
  const [layout, setLayout] = useState("table");
  const stageRef = useRef(null);
  const zoomRef = useRef(null);
  const worldRef = useRef(null);
  const infoRef = useRef(null);
  /* Where the card being read goes while the GPU is drawing the rest: a real
     card, laid over the canvas at the place the scene projects it to. A
     texture at twelve times life size is mush and a texture has no buttons. */
  const focusRef = useRef(null);

  /* The tour: the camera turning and tilting on its own until someone takes it
     back, which the stage reports so the button can come back up. */
  const [playing, setPlaying] = useState(false);
  const stopPlaying = useCallback(() => setPlaying(false), []);

  /* Follow: which way the cards face. Off, each faces the way its arrangement
     put it, so the far side of a sphere shows its cards' backs. On, every card
     turns to the middle of the screen and stays there while the scene is
     turned. */
  const [following, setFollowing] = useState(false);

  /* The card the keyboard is on, and - when one has been taken up - the one
     the list is holding. They are the same card.
     -----------------------------------------------------------------------
     Held by ID, not by position. A shelf is not a fixed set: typing in the
     search, opening a lens or picking a severity rebuilds it, and the card
     that was at index 7 before is somebody else now - so the selection used to
     jump to a different machine without anyone touching it, and Enter opened
     whatever had arrived in that slot. The index below is derived: where the
     selected card IS at this moment, or -1 once it is no longer on the stage.
     ----------------------------------------------------------------------- */
  const [selectedId, setSelectedId] = useState(null);

  /* A page that passes no ids is given positions for them, so the behaviour
     without ids is exactly what it was. */
  const keys = useMemo(
    () => (ids.length === count ? ids : Array.from({ length: count }, (unused, i) => `#${i}`)),
    [ids, count],
  );

  const selected = useMemo(
    () => (selectedId == null ? -1 : keys.indexOf(selectedId)),
    [keys, selectedId],
  );

  /* ...and everything that used to say "select index 7" still can: it is
     turned into the id standing there now. */
  const setSelected = useCallback((index) => {
    setSelectedId(typeof index === "number" && index >= 0 && index < keys.length ? keys[index] : null);
  }, [keys]);

  const [shelved, setShelved] = useState(false);

  /* Which card is being read close up - kept here only so the card can show
     it; the camera's own copy lives in the stage. */
  const [focusedIndex, setFocusedIndex] = useState(-1);
  /* And which one the pointer is over, for the preview column beside it. */
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  /* Which stage draws the scene.
     -----------------------------------------------------------------------
     Both draw the same scene now - the same arrangements, the same camera,
     the same everything the stage answers to (see useForgeStageCore) - so
     this is a choice about how the cards are PAINTED and nothing else: as
     elements the browser lays out and composites, or as quads the GPU draws.

     The GPU is the default, because a shelf of three hundred cards is three
     hundred elements each carrying a transform the browser has to composite,
     and the same shelf is one draw call per card to a machine built for it.
     Where there is no WebGPU there are still cards: the choice falls back,
     rather than the page doing so. */
  const [mode, setMode] = useState(() => {
    const canGpu = typeof navigator !== "undefined" && !!navigator.gpu;
    try {
      const said = localStorage.getItem(rendererKey);
      if (said === "css") return "css";
      if (said === "gpu") return canGpu ? "gpu" : "css";
      return canGpu ? "gpu" : "css";
    } catch (e) { return canGpu ? "gpu" : "css"; }
  });
  useEffect(() => {
    try { localStorage.setItem(rendererKey, mode); } catch (e) { /* private window */ }
  }, [rendererKey, mode]);

  /* The keyboard starts on the first card, so the arrows have somewhere to go
     from and the preview has something to show the moment the list arrives -
     and comes back to it if the card it was on leaves the stage, which is what
     a search narrowing past it does. */
  useEffect(() => {
    if (!count) { if (selectedId != null) setSelectedId(null); return; }
    if (selected < 0) setSelectedId(keys[0]);
  }, [count, selected, selectedId, keys]);

  /* One step through the list, whether it came from an arrow key or the
     wheel - one at a time, or five. It wraps, like the list itself. */
  const stepShelf = useCallback((dir) => {
    setHoveredIndex(-1);
    if (!count) { setSelectedId(null); return; }
    const from = selected < 0 ? 0 : selected;
    setSelectedId(keys[(from + dir + count) % count]);
  }, [count, selected, keys]);

  /* What a card answers, reached through refs: the handlers are defined below
     - they are about the selection, which the stages know nothing about - and
     an argument list mentioning them would be read before they exist. */
  const pick = useRef(() => {});
  const open = useRef(() => {});
  const menu = useRef(null);

  /* The two stages.
     -----------------------------------------------------------------------
     Both are built, and exactly one of them is awake: `enabled` is what tells
     a stage to bind the pointer, lay the cards out and paint, and the one that
     is not drawing binds nothing at all - their handlers live on the same
     element, and a stage that is not drawing must not be taking the hand's
     events either.

     Built rather than chosen because they are hooks and a hook cannot be
     called conditionally; the sleeping one costs two refs and no frames. */
  const commons = {
    layout,
    shelf: -1,
    autoplay: playing,
    follow: following,
    onInteract: stopPlaying,
    onShelfStep: stepShelf,
  };

  /* The rail owns the cards while one is taken up, so neither stage has
     anything to lay out or to answer for. */
  const awake = !shelved;

  const css = useForgeWikiStage(stageRef, zoomRef, worldRef, {
    ...commons,
    enabled: awake && mode === "css",
    count: awake && mode === "css" ? count : 0,
    /* which card is bigger than the rest, and whose neighbours give way */
    selected,
  });

  const gpu = useForgeGpuStage(stageRef, {
    ...commons,
    enabled: awake && mode === "gpu",
    count: awake && mode === "gpu" ? count : 0,
    tiles, looks, query,
    selected,
    overlayRef: focusRef,
    overlayIndex: focusedIndex,
    /* A card on the canvas is not an element, so the handlers its DOM twin
       would have carried are handed to the stage and the ray decides which
       card they are about. The same functions either way, so a click means
       the same thing in both renderers. */
    onPick: (e, i) => pick.current(e, i),
    onOpen: (e, i) => open.current(e, i),
    onHover: (i) => setHoveredIndex(i),
    onMenu: (e, i) => menu.current?.(e, i),
  });

  const stage = mode === "gpu" ? gpu : css;
  const { resetCamera, fitToScreen, focusCard, bringIntoView, project } = stage;

  /* Anything that acts on the scene ends the list - and with it whatever was
     open beside it. A tour, a change of arrangement, a fit, a switch of
     renderer, another search: each is about the set rather than about the one
     card being read, and leaving the reading panel up over a scene that has
     moved on is how a page starts lying. */
  const leaveStack = useCallback(() => setShelved(false), []);

  /**
   * The next card in a direction, by where the cards actually are on screen.
   *
   * Read from the rendered boxes rather than from the arrangement, because
   * what "to the right" means depends on how the scene has been turned: on a
   * sphere seen from above, the card to the right of this one is not the next
   * one round the spiral. Candidates are those far enough along the axis being
   * moved on, and the nearest of them wins - with the sideways distance
   * counted double, so a step right prefers a neighbour in this row to one a
   * row down.
   */
  const stepSelection = useCallback((dx, dy) => {
    /* Where the cards are ON SCREEN, which is what "to the right" is about -
       asked of the scene rather than of the document. It used to be read off
       the cards' bounding boxes, which worked only while every card was an
       element; the projection answers the same question for both renderers,
       and answers it about where the cards are GOING rather than where they
       happen to be mid-tween. */
    const places = stage.objects.current;
    const cards = places.map((o, i) => {
      const p = project(stage.targets.current[i] || o);
      return p ? { x: p.x, y: p.y } : null;
    });
    if (!cards.length) return;

    if (selected < 0 || !cards[selected]) {
      // nothing selected yet: start from whatever is nearest the middle, which
      // the projection puts at the origin
      let best = -1;
      let near = Infinity;
      cards.forEach((p, i) => {
        if (!p) return;
        const d = Math.hypot(p.x, p.y);
        if (d < near) { near = d; best = i; }
      });
      if (best < 0) return;
      setSelected(best);
      bringIntoView(best);
      return;
    }

    const from = cards[selected];
    let best = -1;
    let score = Infinity;
    cards.forEach((p, i) => {
      if (i === selected || !p) return;
      const ax = p.x - from.x;
      const ay = p.y - from.y;
      const along = ax * dx + ay * dy;          // how far in the direction asked for
      if (along < 12) return;                    // behind or beside: not this way
      const aside = Math.abs(ax * dy - ay * dx); // and how far off that line
      const s = along + aside * 2;
      if (s < score) { score = s; best = i; }
    });
    /* Nothing that way - the end of a row, or a stage zoomed in far enough
       that what is "that way" is off it entirely. Then the press is spent
       bringing the card being read back into view, and the next one walks on
       from there with its neighbours on screen to be found. */
    if (best < 0) { bringIntoView(selected); return; }

    setSelected(best);
    /* ...and if the walk has reached the edge of the stage, the stage comes
       with it. Nothing moves while the next card is already on screen. */
    bringIntoView(best);
  }, [selected, setSelected, bringIntoView, project, stage]);


  /* What the panel beside the list is showing, as an index.
     -----------------------------------------------------------------------
     Kept a beat behind the selection: walking the list with the wheel changes
     the head several times a second, and mounting a whole document - or a
     machine's whole detail - for each one on the way past would fetch per
     step. It settles first, then the panel follows. */
  const [stackIndex, setStackIndex] = useState(-1);
  useEffect(() => {
    if (!shelved || selected < 0 || selected >= count) { setStackIndex(-1); return undefined; }
    const at = setTimeout(() => setStackIndex(selected), settleMs);
    return () => clearTimeout(at);
  }, [shelved, selected, count, settleMs]);

  /* Walking the matches, one Enter at a time.
     -----------------------------------------------------------------------
     Typing used to take the camera with it: a beat after each keystroke the
     first match was focused, so writing one word dragged the stage through
     every card whose name started the same way, and the card you were about to
     reach kept being replaced by a better guess. Typing now only lights the
     matches where they stand. Enter is what goes to one - the first, then the
     next, then round again - which is what a search in any editor does.

     The cursor is state rather than a ref because the field says where you are
     in them: "3 / 12" is the whole of why pressing it again makes sense. */
  const [matchAt, setMatchAt] = useState(-1);
  useEffect(() => { setMatchAt(-1); }, [matches]);

  const stepMatch = useCallback((list = matches) => {
    if (!list || !list.length) return -1;
    const next = (matchAt + 1) % list.length;
    const index = list[next];
    setMatchAt(next);
    leaveStack();
    setSelected(index);
    setFocusedIndex(index);
    focusCard(index, { toggle: false });
    return next;
  }, [matches, matchAt, setSelected, focusCard, leaveStack]);

  /* What a click means.
     -----------------------------------------------------------------------
     Once: look at this one. The card comes to the middle of the stage at
     reading size and whatever stood in front of it steps aside - the cheap,
     reversible gesture, and clicking it again puts it back. Twice: open it -
     the cards become a rail and what the card describes opens beside them.

     On the rail the first of those has nowhere to go: the cards are a list
     there, so a click is simply which one is being read, and a double click
     is the way back out to the arrangement. */
  const onCardClick = useCallback((e, index) => {
    if (e.target.closest(".MuiInputBase-root, button, [role='combobox'], a")) return;
    setSelected(index);
    /* Said out loud, because picking a card and the selected index changing
       are not the same event: clicking the card that is already selected
       changes no state at all, and a page that put something away wants to
       hear about that click too (see DiscoveryCards' detail column). */
    if (onPicked) onPicked(index);
    if (shelved) return;
    setFocusedIndex(focusCard(index, { toggle: true }) ? index : -1);
  }, [shelved, setSelected, focusCard, onPicked]);

  /* The one place taking a card up is decided, so the double click and Enter
     cannot come to different answers. */
  const takeUp = useCallback((index) => {
    setSelected(index);
    setFocusedIndex(-1);
    setShelved(v => !v);
  }, [setSelected]);

  pick.current = onCardClick;

  const onCardDoubleClick = useCallback((e, index) => {
    if (e.target.closest(".MuiInputBase-root, button, [role='combobox'], a")) return;
    takeUp(index);
  }, [takeUp]);

  open.current = onCardDoubleClick;
  /* The row menu, for the page that has one. On a canvas there is no element
     to open it against, so the stage is handed the card's index and the page
     is given back whatever it needs to place the menu. */
  menu.current = onCardMenu ? (e, index) => { setSelected(index); onCardMenu(e, index); } : null;

  /* No tab stops on the stage's controls.
     -----------------------------------------------------------------------
     The page is driven by the pointer and by the keys the info sheet lists;
     tabbing through a hundred and twenty cards' pickers to reach the search
     box is not a way anyone would want to use it. The search keeps its stop,
     since typing is the one thing that needs the caret.

     Done here rather than in the markup because some of these buttons come
     from shared components (the band's refresh, MUI's selects) whose props
     the page does not own. It runs after every render, which is also when new
     cards' controls appear. */
  useEffect(() => {
    const root = rootSelector && document.querySelector(rootSelector);
    if (!root) return;
    root.querySelectorAll('button, [role="button"], [role="combobox"], [tabindex="0"]').forEach(el => {
      if (el.closest(".forge-wiki-search") && el.tagName === "INPUT") return;
      el.tabIndex = -1;
    });
  });

  /* The sheet of keys hangs under the info square, but it is drawn inside the
     stage - the band's plate clips anything below it - so the two are lined up
     by measurement: where the square is, as an offset into the stage, handed
     over as a custom property. Re-measured whenever the band can have moved. */
  useEffect(() => {
    const place = () => {
      const stage = stageRef.current;
      const info = infoRef.current;
      if (!stage || !info) return;
      const s = stage.getBoundingClientRect();
      const i = info.getBoundingClientRect();
      stage.style.setProperty("--forge-wiki-info-x", `${Math.round(i.left - s.left)}px`);
    };

    place();
    window.addEventListener("resize", place);
    let ro;
    if (typeof ResizeObserver !== "undefined" && stageRef.current) {
      ro = new ResizeObserver(place);
      ro.observe(stageRef.current);
    }
    return () => { window.removeEventListener("resize", place); ro?.disconnect(); };
  });

  /* When Space was last pressed, so two in quick succession can mean something
     other than two fits. */
  const lastSpace = useRef(0);

  /* Is a menu or a dialog actually open?
     -----------------------------------------------------------------------
     Not "is one in the document": MUI keeps a closed dialog mounted, marked
     `MuiModal-hidden` and `aria-hidden`, and a page that mounts one - the
     applications page keeps two, the editor and the one behind it - would
     otherwise have every key on this stage dead from the moment it loaded,
     because the stage was waiting for a dialog that was never open. */
  const overlayOpen = useCallback(() => !!document.querySelector(
    '.MuiPopover-root:not(.MuiModal-hidden):not([aria-hidden="true"]),'
    + ' .MuiModal-root:not(.MuiModal-hidden):not([aria-hidden="true"])',
  ), []);

  /* The scene's shortcuts. Read from `code`, not `key`: with Shift held, the 0
     and 9 keys report as ) and ( on most layouts.

     Space is the one that has to be careful. It is a page key only while the
     page itself has the keyboard: pressed on a button it presses that button,
     pressed in a field it types, and with a select's menu open it belongs to
     the menu. In each of those cases it is left alone. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
        const el = e.target;
        if (el && el.closest && el.closest(
          "input, textarea, select, button, [role='combobox'], [role='menu'], [contenteditable='true']",
        )) return;
        // a menu or a dialog is open somewhere: the key is theirs
        if (overlayOpen()) return;
        e.preventDefault();
        setFocusedIndex(-1);

        // Once fits what is on the stage from where the camera stands. Twice,
        // quickly, is the way back to the beginning: the arrangement as it is
        // drawn, square on and life size.
        const now = performance.now();
        const twice = now - lastSpace.current < 420;
        lastSpace.current = twice ? 0 : now;
        leaveStack();
        /* Framing the whole arrangement means nothing in particular is being
           read - so whatever was taken up is put down, which on the GPU stage
           is the card flying back to its place. */
        setFocusedIndex(-1);
        if (twice) { setPlaying(false); resetCamera(); } else { fitToScreen(); }
        return;
      }

      // Escape puts the view back as the arrangement draws it: square on, life
      // size, nothing chosen, nothing focused and nothing pushed aside.
      if (e.code === "Escape") {
        if (overlayOpen()) return;
        leaveStack();
        setFocusedIndex(-1);
        setPlaying(false);
        resetCamera();
        return;
      }

      /* The arrows walk the list. Off it they walk the arrangement itself, by
         where the cards are on screen; on it they walk the column - one at a
         time down and up, five at a time left and right. */
      const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (arrows[e.code] && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const el = e.target;
        if (el && el.closest && el.closest("input, textarea, [contenteditable='true']")) return;
        if (overlayOpen()) return;
        e.preventDefault();
        setPlaying(false);
        // the keyboard has the floor now, so the preview follows the selection
        // rather than whatever the pointer happens to be resting on
        setHoveredIndex(-1);
        if (shelved) {
          /* On the list, up and down are the next one and the one before;
             left and right are five at a time, which is how someone gets down
             a list of two hundred without holding a key. */
          const step = { ArrowUp: -1, ArrowDown: 1, ArrowLeft: -5, ArrowRight: 5 }[e.code];
          stepShelf(step);
        } else {
          stepSelection(...arrows[e.code]);
        }
        return;
      }

      // Enter takes the selected card up, the way clicking it does
      if (e.code === "Enter" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const el = e.target;
        if (el && el.closest && el.closest("input, textarea, button, [role='combobox'], a")) return;
        if (overlayOpen()) return;
        if (selected < 0) return;
        e.preventDefault();
        takeUp(selected);
        return;
      }

      // P - the tour, started and stopped
      if (e.code === "KeyP" && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
        const el = e.target;
        if (el && el.closest && el.closest("input, textarea, [contenteditable='true']")) return;
        e.preventDefault();
        leaveStack();
        setPlaying(v => !v);
        return;
      }

      // F - turn the cards to the screen, or let the arrangement place them
      if (e.code === "KeyF" && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
        const el = e.target;
        if (el && el.closest && el.closest("input, textarea, [contenteditable='true']")) return;
        e.preventDefault();
        leaveStack();
        setFollowing(v => !v);
        return;
      }

      // 1 2 3 - the arrangements, in the order the picker offers them
      if (!e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
        const at = ["Digit1", "Digit2", "Digit3"].indexOf(e.code);
        if (at >= 0) {
          const el = e.target;
          if (el && el.closest && el.closest("input, textarea, [contenteditable='true']")) return;
          if (overlayOpen()) return;
          e.preventDefault();
          setFocusedIndex(-1);
          leaveStack();
          if (layout === WIKI_LAYOUTS[at].key) resetCamera(); else setLayout(WIKI_LAYOUTS[at].key);
          return;
        }
      }

      if (!e.ctrlKey || !e.shiftKey || e.altKey || e.metaKey) return;
      if (e.code === "KeyG") {
        /* Which stage draws the scene. It used to be a button on every card
           page's toolbar; it is a thing you decide once about this machine,
           not something to be chosen beside the arrangement, so it is a key -
           and the info sheet is where it is written down. */
        e.preventDefault();
        setShelved(false);
        setMode(m => (m === "gpu" ? "css" : "gpu"));
        return;
      }
      if (e.code === "Digit0") {
        if (!onToggleSamples) return;
        e.preventDefault();
        leaveStack();
        onToggleSamples();
      } else if (e.code === "Digit9") {
        e.preventDefault();
        leaveStack();
        fitToScreen();
        setFocusedIndex(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fitToScreen, resetCamera, layout, shelved, selected, stepSelection, stepShelf,
      leaveStack, onToggleSamples, takeUp, overlayOpen]);

  /* Switching arrangement, renderer or anything else that moves the scene goes
     through here, so no page has to remember to drop the rail first. */
  const chooseLayout = useCallback((key) => {
    setFocusedIndex(-1);
    leaveStack();
    if (layout === key) resetCamera(); else setLayout(key);
  }, [layout, leaveStack, resetCamera]);

  const toggleRenderer = useCallback(() => {
    leaveStack();
    setMode(m => (m === "gpu" ? "css" : "gpu"));
  }, [leaveStack]);

  const togglePlaying = useCallback(() => { leaveStack(); setPlaying(p => !p); }, [leaveStack]);
  const toggleFollowing = useCallback(() => { leaveStack(); setFollowing(v => !v); }, [leaveStack]);

  /* How a card should read, given everything above: open when it is pointed
     at, chosen or focused; lit or dimmed by what the search is pointing at. */
  const cardState = useCallback((index) => {
    const lit = matches.length > 0 && matches.includes(index);
    return {
      open: hoveredIndex === index || focusedIndex === index || selected === index,
      selected: selected === index,
      focused: focusedIndex === index,
      lit,
      unlit: matches.length > 0 && !lit,
    };
  }, [matches, hoveredIndex, focusedIndex, selected]);

  /* The class list those states come to, so two pages cannot spell them
     differently. */
  const cardClass = useCallback((index, extra = "") => {
    const s = cardState(index);
    return `forge-wiki-card${extra ? ` ${extra}` : ""}`
      + (s.focused ? " is-focused" : "")
      + (s.selected ? " is-selected" : "")
      + (s.open ? " is-open" : "")
      + (s.lit ? " is-match" : "")
      + (s.unlit ? " is-unmatched" : "");
  }, [cardState]);

  /* Which card the preview is about: the one under the pointer, or the chosen
     one when the pointer is elsewhere. */
  const previewIndex = useMemo(
    () => (hoveredIndex >= 0 ? hoveredIndex : selected),
    [hoveredIndex, selected],
  );

  return {
    // the stage's own elements
    stageRef, zoomRef, worldRef, infoRef, focusRef,
    // what it is doing
    layout, setLayout, mode, setMode, playing, following,
    selected, setSelected, selectedId, shelved, setShelved,
    focusedIndex, setFocusedIndex, hoveredIndex, setHoveredIndex,
    previewIndex, stackIndex,
    /* the search's own: how far through the matches Enter has walked, and the
       step that walks them */
    matchAt, stepMatch,
    // and what can be done to it
    onCardClick, onCardDoubleClick,
    chooseLayout, toggleRenderer, togglePlaying, toggleFollowing,
    leaveStack, resetCamera, fitToScreen, focusCard,
    cardState, cardClass,
  };
}

export default useForgeCardScene;
